import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { QueryChartTypeKey } from "../schemas/kpi-props.schema";
import { kpiService } from "./kpi-service";
import { ReactComponent as IconSinestralidad } from "../../../assets/img/icon/dashboard/diagnosticos_presentados.svg";
import { ReactComponent as IconMenu } from "../../../assets/img/icon/dashboard/dashboard_button_modal.svg";
import { ProdesegButton, ProdesegButtonEnumTypes } from "../../../components/ProdesegButton/ProdesegButton";
import { StatsModal } from "./StatsModal";
import { ChartData, ChartOptions } from "chart.js";
import { Spinner } from "reactstrap";
import { formatCurrency } from "../schemas/dashboard.utils";
import { useBoolean } from "../../../custom-hooks/useBoolean";
import { CubeBasicFilters, CubeSummaryResponse } from "../schemas/kpi.types";

export const KPIDoughnut: React.FC<{
    cubeFilters: CubeBasicFilters;
    typeInquiry: QueryChartTypeKey;
    titleChart: string;
}> = ({ cubeFilters, typeInquiry, titleChart }) => {
    const [chartData, setChartData] = useState<ChartData<"doughnut">>({
        labels: [],
        datasets: [],
    });

    const [rawStats, setRawStats] = useState<CubeSummaryResponse[]>([]);
    const { value: isModalOpen, toggle: toggleModal } = useBoolean(false);
    const { idClient, idQuotes, policy } = cubeFilters;

    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const doughnutData: CubeSummaryResponse[] = await kpiService.fetchDiagnosis(cubeFilters);
                setRawStats(doughnutData);
                const total = doughnutData.reduce((acc, curr) => acc + curr.amount, 0);

                setChartData({
                    labels: doughnutData.map(d => d.type),
                    datasets: [
                        {
                            label: "Siniestros",
                            data: doughnutData.map(doughnut => (total > 0 ? (doughnut.amount / total) * 100 : 0)),
                            backgroundColor: ["#087678", "#189B9C", "#4DBDBF", "#A6DEDF", "#E9E572"],
                            borderWidth: 1,
                        },
                    ],
                });
            } catch (error) {
                console.error("Error al cargar el gráfico:", error);
            } finally {
                setLoading(false);
            }
        };

        if (idClient) {
            void loadData();
        }
    }, [idClient, idQuotes, policy]);

    const options: ChartOptions<"doughnut"> = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: 50,
        },
        plugins: {
            legend: { display: false },
            datalabels: {
                display: true,
                anchor: "end",
                align: "end",
                offset: 5,
                formatter: (value: number) => {
                    return value.toFixed(1) + "%";
                },
                font: {
                    weight: "bold",
                    size: 10,
                },
            },
        },
        cutout: "65%",
    };

    return (
        <div className="siniestralidad-container card-fixed-height">
            {loading ? (
                <div className="text-center py-4">
                    <Spinner color="primary" />
                </div>
            ) : (
                <>
                    <div className="header-section-doughnut">
                        <div className="header-title-group">
                            <div className="icon-wrapper">
                                <IconSinestralidad />
                            </div>
                            <h2 className="title-label-doughnut">{titleChart}</h2>
                        </div>

                        <ProdesegButton
                            type={ProdesegButtonEnumTypes.PrimaryLight}
                            Icon={IconMenu}
                            onlyIcon
                            isOutline
                            onClick={toggleModal}
                            aria-label="Abrir detalle"
                        />
                    </div>
                    <div className="scrollable-body">
                        <div className="chart-wrapper">
                            <Doughnut data={chartData} options={options} />
                        </div>

                        <div className="summary-list">
                            {rawStats.map((item, index) => {
                                const dataset = chartData.datasets[0];
                                const bgColor: string = Array.isArray(dataset.backgroundColor)
                                    ? dataset.backgroundColor[index]
                                    : dataset.backgroundColor;

                                return (
                                    <div key={Number(index)} className="summary-row">
                                        <div className="summary-left">
                                            <span className="status-dot" style={{ backgroundColor: bgColor }} />
                                            <span className="summary-name">{item.type}</span>
                                        </div>
                                        <span className="summary-total">{formatCurrency(item.amount)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
            <StatsModal
                isOpen={isModalOpen}
                toggle={toggleModal}
                title={titleChart}
                data={rawStats}
                labelField="type"
                valueField="amount"
                valueCountField={"total"}
                queryType={typeInquiry}
            />
        </div>
    );
};
